import { OrderKind, StopStatus, TimeFormat } from "@app/domain";
import { formatDateTime, formatEventTime } from "@app/shared/utils/formatDate";
import { Languages } from "@app/types/multilingual";
import { t } from "@app/utils/translation";
import { MapEtaOverlay } from "../MapView.types";

const ETA_ELIGIBLE_KINDS: OrderKind[] = ["ondemand", "delivery", "pickup", "pickupDelivery"];
const ETA_ELIGIBLE_STATUS_CODES = [
  "Started",
  "Arrived",
  "PickedUp",
  "ReadyForDelivery",
  "OutForDelivery",
  "PickupRouted",
  "DeliveryRouted",
];
const PICKUP_DELIVERY_DROPOFF_ETA_STATUS_CODES = [
  "DeliveryRouted",
  "ReadyForDelivery",
  "OutForDelivery",
];

export type ComputeMarkerEtaInput = {
  orderKind: OrderKind;
  statusCode: string;
  etaAt: number | null;
  userActive: boolean;
  isDropoffTracking: boolean;
  isLastDropOff: boolean;
  pickupMarkerId: string | null;
  lastDropoffMarkerId: string | null;
  /** The last dropoff's own status, in route order — `null` when the order
   * has no dropoffs yet. Once it reads "done" (Delivered), the delivery ETA
   * is stale even if `etaAt` is still populated, so it's checked ahead of
   * everything else below (mirrors `computeTrackingStatusLabel`'s
   * `dropoffStatus === "done"` check, same reasoning: the order-level
   * `statusCode` can still read "PickedUp" after the last dropoff is
   * already delivered). */
  lastDropoffStatus: StopStatus | null;
  language: Languages;
  timeFormat: TimeFormat;
};

/**
 * Pure marker-target selection for the ETA overlay, including the
 * order-kind-specific pickup/delivery status rules. Kept dependency-free
 * (no hooks) so the rule itself is testable on its own —
 * `hooks/useMarkerEta.ts` is the only thing that wires it to React state.
 */
export function computeMarkerEta(input: ComputeMarkerEtaInput): MapEtaOverlay {
  const {
    orderKind,
    statusCode,
    etaAt,
    userActive,
    isDropoffTracking,
    isLastDropOff,
    pickupMarkerId,
    lastDropoffMarkerId,
    lastDropoffStatus,
    language,
    timeFormat,
  } = input;

  const eligible =
    ETA_ELIGIBLE_KINDS.includes(orderKind) &&
    ETA_ELIGIBLE_STATUS_CODES.includes(statusCode) &&
    userActive &&
    etaAt !== null &&
    lastDropoffStatus !== "done" &&
    !(orderKind === "pickupDelivery" && statusCode === "PickedUp") &&
    (statusCode !== "PickedUp" || !isDropoffTracking || isLastDropOff);

  if (!eligible || etaAt === null) return null;

  const time = formatDateTime(etaAt, language);

  if (
    statusCode === "PickedUp" ||
    (orderKind === "pickupDelivery" && PICKUP_DELIVERY_DROPOFF_ETA_STATUS_CODES.includes(statusCode))
  ) {
    return lastDropoffMarkerId
      ? { markerId: lastDropoffMarkerId, label: `${t("EstDelivery", language)}: ${time}` }
      : null;
  }

  return pickupMarkerId ? { markerId: pickupMarkerId, label: `${t("EstPickup", language)}: ${time}` } : null;
}
